import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { v4 as uuidv4 } from 'uuid'
import type { RecurringExpense } from '@/types'
import { getCurrentWorkspaceId } from '@/types'
import { getRecurringExpenseRepository } from '@/repository'

interface RecurringExpenseState {
  expenses: RecurringExpense[]
  isLoading: boolean
  isInitialized: boolean
  /**
   * v0.8.48 — Same pattern debtStore established in v0.8.45. Pre-fix a
   * bare `catch {}` swallowed getAll() errors (RLS, schema-cache,
   * network) and left the store in an empty-but-"initialized" state
   * with no signal. The CloudSyncError banner reads debtStore's
   * `initError`; if recurringExpenses fails independently, this
   * surfaces via console + could be wired to the banner later.
   */
  initError: string | null
}

interface RecurringExpenseActions {
  initialize: () => Promise<void>
  /** v0.8.48 — Manual retry after a failed initialize. */
  retryInitialize: () => Promise<void>
  addExpense: (
    data: Omit<RecurringExpense, 'id' | 'createdAt' | 'updatedAt' | 'workspaceId'>
  ) => Promise<void>
  updateExpense: (
    id: string,
    patch: Partial<Omit<RecurringExpense, 'id' | 'createdAt'>>
  ) => Promise<void>
  removeExpense: (id: string) => Promise<void>
  bulkImport: (expenses: RecurringExpense[]) => Promise<void>
  /** See `debtStore.reset` — used by `bindReposToSession`. */
  reset: () => void
}

export const useRecurringExpenseStore = create<RecurringExpenseState & RecurringExpenseActions>()(
  immer((set, get) => ({
    expenses: [],
    isLoading: false,
    isInitialized: false,
    initError: null,

    initialize: async () => {
      if (get().isInitialized) return
      set((s) => {
        s.isLoading = true
        s.initError = null
      })
      try {
        const expenses = await getRecurringExpenseRepository().getAll()
        set((s) => {
          s.expenses = expenses
          s.isLoading = false
          s.isInitialized = true
          s.initError = null
        })
      } catch (err) {
        // v0.8.48 — surface the failure instead of silently swallowing.
        // Same rationale as debtStore v0.8.45.
        const message = err instanceof Error ? err.message : String(err)
        console.error('[DebtOS] recurringExpenseStore.initialize failed:', message)
        set((s) => {
          s.isLoading = false
          s.initError = message
        })
      }
    },

    retryInitialize: async () => {
      set((s) => {
        s.isInitialized = false
        s.initError = null
      })
      await get().initialize()
    },

    addExpense: async (data) => {
      const now = new Date().toISOString()
      const newExpense: RecurringExpense = {
        ...data,
        id: uuidv4(),
        workspaceId: getCurrentWorkspaceId(),
        createdAt: now,
        updatedAt: now,
      }
      await getRecurringExpenseRepository().upsert(newExpense)
      set((s) => {
        s.expenses.push(newExpense)
      })
    },

    updateExpense: async (id, patch) => {
      const existing = get().expenses.find((e) => e.id === id)
      if (!existing) return
      const updated: RecurringExpense = {
        ...existing,
        ...patch,
        updatedAt: new Date().toISOString(),
      }
      await getRecurringExpenseRepository().upsert(updated)
      set((s) => {
        const idx = s.expenses.findIndex((e) => e.id === id)
        if (idx >= 0) s.expenses[idx] = updated
      })
    },

    removeExpense: async (id) => {
      await getRecurringExpenseRepository().delete(id)
      set((s) => {
        s.expenses = s.expenses.filter((e) => e.id !== id)
      })
    },

    bulkImport: async (expenses) => {
      await getRecurringExpenseRepository().bulkReplace(expenses)
      set((s) => {
        s.expenses = expenses
      })
    },

    reset: () => {
      set((s) => {
        s.expenses = []
        s.isLoading = false
        s.isInitialized = false
        s.initError = null
      })
    },
  }))
)
